import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { FileText, Eye, Loader2, Building2, AlertTriangle, Combine } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const LETTER_SECTIONS = ['code_violations', 'open_permits'];
const SHORT = { code_violations: 'Code Enforcement', open_permits: 'Building Permit' };
const titleOf = (sections) => (sections.length === 2 ? 'Code Enforcement & Building Permit Request' : `${SHORT[sections[0]]} Request`);
const entityOfCandidate = (c) => ({
  service_entity_id: c.service_entity_id || null, entity_name: c.department_name, address: c.address || '', phone: c.phone || '',
  fax: c.fax || '', email: c.email || '', jurisdiction_name: c.jurisdiction_name || '', jurisdiction_level: c.jurisdiction_level || '',
});
const entityOfService = (s) => ({
  service_entity_id: s.id, entity_name: s.entity_name, address: s.physical_address || '', phone: s.phone || '', phone_ext: s.phone_ext || '',
  fax: s.fax || '', email: s.email || '', jurisdiction_name: s.jurisdiction_name || '', jurisdiction_level: s.jurisdiction_level || '',
});

/** Pure helper shared with the modal: letters the batch will produce for the chosen candidates. */
export const planLetters = (chosen, combine, combineEntity) => {
  const bySection = Object.fromEntries(chosen.filter((c) => LETTER_SECTIONS.includes(c.section)).map((c) => [c.section, c]));
  const sections = LETTER_SECTIONS.filter((s) => bySection[s]);
  if (!sections.length) return [];
  if (combine && sections.length === 2) return [{ sections, entity: combineEntity, fees: sections.map((s) => bySection[s].fee), combined: true }];
  return sections.map((s) => ({ sections: [s], entity: entityOfCandidate(bySection[s]), fees: [bySection[s].fee], combined: false }));
};

const EntityLine = ({ entity, testId }) => (
  <div className="text-[12px] text-slate-600 truncate" data-testid={testId}>
    {entity?.entity_name ? <>{entity.entity_name}{entity.address ? ` · ${entity.address}` : ''}</> : <span className="text-rose-600 font-medium">No entity selected</span>}
    {entity?.entity_name && !entity.address && <span className="ml-1 text-rose-600 font-medium">· no mailing address</span>}
  </div>
);

/* MLS-MAILAWAY-LETTER-001 — request-letter plan: one letter per section, or a combined letter addressed to a
   processor-selected entity (section entities + active Jurisdiction Directory entities). Never auto-combines. */
export const MlsMailAwayLetters = ({ orderId, chosen, combine, setCombine, combineEntity, setCombineEntity, dateSent, buildItems }) => {
  const letterChosen = chosen.filter((c) => LETTER_SECTIONS.includes(c.section));
  const both = LETTER_SECTIONS.every((s) => letterChosen.some((c) => c.section === s));
  const [directory, setDirectory] = useState([]);
  const [previewing, setPreviewing] = useState(null);
  const jurisdictionIds = useMemo(() => [...new Set(letterChosen.map((c) => c.service_jurisdiction_id).filter(Boolean))], [letterChosen]);

  useEffect(() => {
    if (!both || !combine) return;
    let alive = true;
    const idsFrom = async () => {
      if (jurisdictionIds.length) return jurisdictionIds;
      // fall back to the order's resolved MLS jurisdiction (municipality + county) when entries carry no directory id
      const r = await axios.get(`${API}/api/orders/${orderId}/mls-jurisdiction`).catch(() => null);
      return [r?.data?.municipality?.id, r?.data?.county?.id].filter(Boolean);
    };
    idsFrom().then((ids) => Promise.all(ids.map((jid) => axios.get(`${API}/api/jurisdictions/${jid}/mls-services`).then((r) => r.data?.services || []).catch(() => []))))
      .then((lists) => { if (alive) setDirectory(lists.flat().filter((s) => s.is_active !== false)); });
    return () => { alive = false; };
  }, [both, combine, jurisdictionIds, orderId]);

  useEffect(() => { if (!both && combine) { setCombine(false); setCombineEntity(null); } }, [both, combine, setCombine, setCombineEntity]);

  const plan = planLetters(chosen, combine, combineEntity);
  if (!letterChosen.length) return null;

  const options = [
    ...letterChosen.map((c) => ({ key: `sec:${c.key}`, label: `${c.department_name} (${SHORT[c.section]} entity)`, entity: entityOfCandidate(c) })),
    ...directory.filter((s) => !letterChosen.some((c) => c.service_entity_id && c.service_entity_id === s.id))
      .map((s) => ({ key: `dir:${s.id}`, label: `${s.entity_name} — ${s.service_type || 'directory'} · ${s.jurisdiction_name || ''}`, entity: entityOfService(s) })),
  ];
  const selectedKey = options.find((o) => combineEntity && o.entity.entity_name === combineEntity.entity_name && (o.entity.service_entity_id || null) === (combineEntity.service_entity_id || null))?.key || '';

  const preview = async (index) => {
    setPreviewing(index);
    const win = window.open('', '_blank'); // opened synchronously in the click so the browser does not block it
    try {
      const body = { items: buildItems(), letter_index: index, date_sent: dateSent || null };
      if (combine && both) body.combine_letter = { entity: combineEntity };
      const res = await axios.post(`${API}/api/orders/${orderId}/components/mls/mail-away/letter-preview`, body, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      if (win) win.location = url; else window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      if (win) win.close();
      let msg = 'Could not generate the letter preview';
      try { const txt = await e.response?.data?.text?.(); const d = JSON.parse(txt || '{}').detail; msg = typeof d === 'string' ? d : (d?.message || msg); } catch { /* keep default */ }
      toast.error(msg);
    } finally { setPreviewing(null); }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 space-y-2" data-testid="mls-ma-letters-panel">
      <div className="flex items-center gap-2">
        <FileText className="w-4 h-4 text-blue-600" />
        <span className="text-[13px] font-semibold text-slate-800">Request letters</span>
        <span className="ml-auto text-[11px] text-slate-500" data-testid="mls-ma-letters-count">{plan.length} PDF{plan.length === 1 ? '' : 's'} will be generated &amp; attached</span>
      </div>
      {both && (
        <label className="flex items-center gap-2 text-[12.5px] font-medium text-slate-700 cursor-pointer select-none" data-testid="mls-ma-combine-toggle-label">
          <input type="checkbox" checked={combine} onChange={(e) => { setCombine(e.target.checked); if (!e.target.checked) setCombineEntity(null); }}
            className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500/40" data-testid="mls-ma-combine-toggle" />
          <Combine className="w-3.5 h-3.5 text-slate-500" /> Same Entity / Combine Request — one letter covering Code Enforcement and Building Permits
        </label>
      )}
      {both && combine && (
        <div className="space-y-1" data-testid="mls-ma-combine-entity-block">
          <select value={selectedKey} onChange={(e) => setCombineEntity(options.find((o) => o.key === e.target.value)?.entity || null)}
            className="w-full h-9 px-2.5 text-[12.5px] border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/40" data-testid="mls-ma-combine-entity-select">
            <option value="">Select the entity handling both sections…</option>
            {options.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
          </select>
          {!combineEntity && <div className="flex items-center gap-1 text-[11px] text-amber-700" data-testid="mls-ma-combine-entity-missing"><AlertTriangle className="w-3 h-3" />Select the responsible entity to generate the combined letter.</div>}
        </div>
      )}
      <div className="space-y-1.5">
        {plan.map((L, i) => (
          <div key={i} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50/70 px-2.5 py-1.5" data-testid={`mls-ma-letter-row-${i}`}>
            <Building2 className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="text-[12.5px] font-semibold text-slate-800" data-testid={`mls-ma-letter-title-${i}`}>{titleOf(L.sections)}</div>
              <EntityLine entity={L.entity} testId={`mls-ma-letter-entity-${i}`} />
            </div>
            <button type="button" onClick={() => preview(i)} disabled={previewing !== null || !L.entity?.entity_name || !L.entity?.address}
              className="inline-flex items-center gap-1 h-7 px-2.5 text-[11.5px] font-semibold rounded-md border border-blue-200 text-blue-700 bg-white hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              data-testid={`mls-ma-letter-preview-${i}`}>
              {previewing === i ? <Loader2 className="w-3 h-3 animate-spin" /> : <Eye className="w-3 h-3" />} Preview PDF
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export const lettersValid = (chosen, combine, combineEntity) =>
  planLetters(chosen, combine, combineEntity).every((L) => L.entity?.entity_name && L.entity?.address);
